/* eslint-disable no-console */

import { updateId } from 'expo-updates';
import { Platform } from 'react-native';

import { Loggable } from '@rbi-ctg/frontend';
import { appVersion, binaryRuntimeAppVersionWithBuildNumber } from 'utils/app-version-info';
import { appStartId, env, getCurrentCommitHash, region } from 'utils/environment';

import { defaultChannels } from './channels';
import { LoggerChannel, LoggerEvent } from './channels/types';
import { LogLevel } from './log-level';
import {
  ErrorCategory,
  ILoggerMiddleware,
  deviceIdMiddleware,
  errorCategoryMiddleware,
  uuidScrubberMiddleware,
} from './middleware';
import { ILogger, ILoggerBaseFields, ILoggerOptions, LogFunction, Method } from './types';

const methods: Method[] = ['debug', 'error', 'fatal', 'info', 'trace', 'warn'];

const prepareMessage = <O extends Loggable>(
  message: O,
  level: LogLevel,
  loggerAttributes: ILoggerBaseFields
) => {
  if (message instanceof Error) {
    return Object.assign({}, { error: message }, loggerAttributes, { level });
  }

  if (typeof message === 'object') {
    return Object.assign({}, message, loggerAttributes, { level });
  }

  return Object.assign({}, { message }, loggerAttributes, {
    level,
  });
};

/**
 * This function is only exported for testing.
 * The default export for this file is a logger
 * instantiated with fields we want to send
 * on _every_ message to logger.
 * We should only use loggers that are
 * a child of the default logger in production.
 */
export function Logger<T extends object, U extends object>(
  fields: T,
  parentFields?: U,
  loggerOptions: ILoggerOptions = {}
): ILogger {
  const { middlewares = [], currentEnv = env(), channels = defaultChannels } = loggerOptions;
  // merge child into parent attributes, then clear undefined attributes
  const loggerAttributes = Object.entries(Object.assign({}, parentFields, fields)).reduce(
    (acc, [key, value]) => (value === undefined ? acc : { ...acc, [key]: value }),
    {}
  );

  const logFunction = (method: Method): LogFunction => message => {
    const level = LogLevel[method];

    type middlewareAttributes = { [key: string]: Loggable };

    // NOTE: If `error` is present uuid-scrubber middleware
    // will replace `message` with `error.message` and
    // append the original `message` property
    // as `originalDeveloperMessage` in attributes
    const attributes = middlewares.reduce<middlewareAttributes>(
      (attr, middleware) => middleware(attr),
      prepareMessage(message, level, loggerAttributes as ILoggerBaseFields) as middlewareAttributes
    );

    const { category } = attributes;

    if (category === ErrorCategory.DoNotLog) {
      return;
    }

    // Normalize once, then let each enabled channel decide eligibility and
    // delivery. Severity (`isEligible`) is intentionally separate from channel
    // enablement (`isEnabled`), so `logger.info` no longer implies Datadog.
    const event: LoggerEvent = { level, method, message, attributes };

    for (const channel of channels) {
      if (channel.isEnabled({ currentEnv }) && channel.isEligible(event)) {
        channel.deliver(event);
      }
    }
  };

  const loggerPublicInterface = methods.reduce<ILogger>((logger, method) => {
    return { ...logger, [method]: logFunction(method), [method.toUpperCase()]: LogLevel[method] };
  }, {} as ILogger);

  Object.assign(loggerPublicInterface, {
    group: console.group.bind(console),
    groupCollapsed: console.groupCollapsed.bind(console),
    groupEnd: console.groupEnd.bind(console),
  });

  return loggerPublicInterface;
}

export const loggerBaseFields: ILoggerBaseFields = {
  platform: Platform?.OS as Platform['OS'],
  region,
  stage: env(),
  appStartId,
  userId: undefined, // this is set by AuthStorage to avoid a circular reference that was causing build issues
  buildInfo: {
    updateId,
    commitHash: getCurrentCommitHash(),
    binaryRuntimeVersion: binaryRuntimeAppVersionWithBuildNumber,
    appVersion,
  },
};

let loggingContext: Record<string, unknown> = {};

const contextChannels: LoggerChannel[] = defaultChannels;

export const addLoggingContext = (context: Object) => {
  loggingContext = { ...loggingContext, ...context };

  for (const channel of contextChannels) {
    channel.syncContext?.(context as Record<string, unknown>);
  }
};

export const removeLoggingContextKeys = (keys: string[]) => {
  loggingContext = Object.fromEntries(
    Object.entries(loggingContext).filter(([key]) => !keys.includes(key))
  );

  for (const channel of contextChannels) {
    channel.removeContextKeys?.(keys);
  }
};

const loggingContextMiddleware = (attributes => {
  return { ...attributes, ...loggingContext };
}) as ILoggerMiddleware;

const DEFAULT_MIDDLEWARES = [
  errorCategoryMiddleware,
  uuidScrubberMiddleware,
  deviceIdMiddleware,
  loggingContextMiddleware,
];

/**
 * The preferred used export is a configured logger
 * with appVersion, brand, env and platform attributes.
 * Add additional interesting logging context to all log statements with `addLoggingContext`
 */
export const defaultLogger = Logger(loggerBaseFields, {}, { middlewares: DEFAULT_MIDDLEWARES });

export const logger = defaultLogger; // VSCODE auto import works better with this.

export default defaultLogger;
